import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  Snackbar,
} from '@mui/material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  exportPulseAnalyticsCards,
  getPulseAnalytics,
  getPulseAnalyticsCards,
  getPulseErrorMessage,
  getPulsePayload,
  isPulseApiSuccess,
  triggerPulseBlobDownload,
} from '../../services/pulseApi';
import {
  PULSE_CATEGORIES,
  formatNumber,
  formatPulseDate,
  formatRate,
  parseAnalyticsPayload,
  parseCardsPayload,
  statusChipColor,
} from './pulseUtils';

const PAGE_SIZE = 30;

const ANALYTICS_SORTS = new Set([
  'created_at',
  'expires_at',
  'submissions',
  'unique_submissions',
  'unique_views',
  'views',
  'skips',
  'awarded',
  'fe_awarded',
  'fe_pending',
  'fe_spent',
  'interaction_rate',
]);

function SummaryCard({ label, value, hint, bgcolor = '#fafafa' }) {
  return (
    <Paper sx={{ p: 2, textAlign: 'center', bgcolor, height: '100%' }}>
      <Typography variant="h4" fontWeight={700}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      {hint ? (
        <Typography variant="caption" color="text.disabled" display="block">
          {hint}
        </Typography>
      ) : null}
    </Paper>
  );
}

/**
 * A view on this screen is a finished decision. Impression counters from an
 * older response are ignored so Views always equals submissions + skips,
 * and the two rates are shares of that total.
 */
function decisionMetrics(row) {
  const submissions = Math.max(0, Math.trunc(Number(row?.submissions) || 0));
  const skips = Math.max(0, Math.trunc(Number(row?.skips) || 0));
  const views = submissions + skips;
  if (views <= 0) return { views: 0, interaction_rate: 0, skip_rate: 0 };
  const interactionTenths = Math.round((submissions * 1000) / views);
  return {
    views,
    interaction_rate: interactionTenths / 10,
    skip_rate: (1000 - interactionTenths) / 10,
  };
}

function PulseAnalytics() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState('');
  const [summary, setSummary] = useState(parseAnalyticsPayload(null));
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);

  const applied = useMemo(() => {
    const sortParam = searchParams.get('sort') || 'created_at';
    return {
      category: searchParams.get('category') || '',
      match_id: searchParams.get('match_id') || '',
      date_from: searchParams.get('date_from') || '',
      date_to: searchParams.get('date_to') || '',
      sort: ANALYTICS_SORTS.has(sortParam) ? sortParam : 'created_at',
      order: searchParams.get('order') === 'asc' ? 'asc' : 'desc',
      offset: Math.max(0, Number(searchParams.get('offset') || 0) || 0),
    };
  }, [searchParams]);

  const [category, setCategory] = useState(applied.category);
  const [matchId, setMatchId] = useState(applied.match_id);
  const [dateFrom, setDateFrom] = useState(applied.date_from);
  const [dateTo, setDateTo] = useState(applied.date_to);

  useEffect(() => {
    setCategory(applied.category);
    setMatchId(applied.match_id);
    setDateFrom(applied.date_from);
    setDateTo(applied.date_to);
  }, [applied]);

  const writeParams = useCallback(
    (next) => {
      const params = {};
      if (next.category) params.category = next.category;
      if (next.match_id) params.match_id = String(next.match_id).trim();
      if (next.date_from) params.date_from = next.date_from;
      if (next.date_to) params.date_to = next.date_to;
      if (next.sort && next.sort !== 'created_at') params.sort = next.sort;
      if (next.order === 'asc') params.order = 'asc';
      if (next.offset) params.offset = String(next.offset);
      setSearchParams(params);
    },
    [setSearchParams]
  );

  const dateFilters = useMemo(
    () => ({
      date_from: applied.date_from || undefined,
      date_to: applied.date_to || undefined,
    }),
    [applied.date_from, applied.date_to]
  );

  // Date + category applied together on both analytics endpoints
  const sharedFilters = useMemo(
    () => ({
      ...dateFilters,
      category: applied.category || undefined,
    }),
    [dateFilters, applied.category]
  );

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const result = await getPulseAnalytics(sharedFilters);
      if (!isPulseApiSuccess(result)) {
        setError(getPulseErrorMessage(result, 'Failed to load Pulse analytics.'));
        setSummary(parseAnalyticsPayload(null));
        return;
      }
      setSummary(parseAnalyticsPayload(getPulsePayload(result)));
      setError('');
    } catch (fetchError) {
      setError(fetchError.message || 'Failed to load Pulse analytics.');
      setSummary(parseAnalyticsPayload(null));
    } finally {
      setSummaryLoading(false);
    }
  }, [sharedFilters]);

  const loadCards = useCallback(async () => {
    setTableLoading(true);
    try {
      const result = await getPulseAnalyticsCards({
        limit: PAGE_SIZE,
        offset: applied.offset,
        match_id: applied.match_id || undefined,
        sort: applied.sort,
        order: applied.order,
        ...sharedFilters,
      });
      if (!isPulseApiSuccess(result)) {
        setError(getPulseErrorMessage(result, 'Failed to load per-card analytics.'));
        setRows([]);
        setTotal(0);
        return;
      }
      const parsed = parseCardsPayload(getPulsePayload(result));
      setRows(parsed.items);
      setTotal(parsed.total);
    } catch (fetchError) {
      setError(fetchError.message || 'Failed to load per-card analytics.');
      setRows([]);
      setTotal(0);
    } finally {
      setTableLoading(false);
    }
  }, [applied.offset, applied.match_id, applied.sort, applied.order, sharedFilters]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    loadCards();
  }, [loadCards]);

  const refresh = () => {
    loadSummary();
    loadCards();
  };

  const copyCardId = async (cardId, event) => {
    event.stopPropagation();
    try {
      await navigator.clipboard.writeText(cardId);
      setSnackbar('Card ID copied');
    } catch {
      setSnackbar('Could not copy Card ID');
    }
  };

  const handleExport = async () => {
    setExporting(true);
    setError('');
    try {
      const { blob, filename } = await exportPulseAnalyticsCards({
        category: applied.category || undefined,
        match_id: applied.match_id || undefined,
        date_from: applied.date_from || undefined,
        date_to: applied.date_to || undefined,
        sort: applied.sort,
        order: applied.order,
      });
      triggerPulseBlobDownload(blob, filename);
      setSnackbar(`Downloaded ${filename}`);
    } catch (exportError) {
      setError(exportError.message || 'Failed to export card analytics.');
    } finally {
      setExporting(false);
    }
  };

  const applyFilters = () => {
    writeParams({
      category,
      match_id: matchId,
      date_from: dateFrom,
      date_to: dateTo,
      sort: applied.sort,
      order: applied.order,
      offset: 0,
    });
  };

  const clearDates = () => {
    setDateFrom('');
    setDateTo('');
    writeParams({
      category,
      match_id: matchId,
      date_from: '',
      date_to: '',
      sort: applied.sort,
      order: applied.order,
      offset: 0,
    });
  };

  const handleSort = (key) => {
    if (!ANALYTICS_SORTS.has(key)) return;
    const order = applied.sort === key && applied.order === 'desc' ? 'asc' : 'desc';
    writeParams({ ...applied, sort: key, order, offset: 0 });
  };

  const chartData = useMemo(
    () =>
      (summary.items || []).map((row) => ({
        name: row.category || row.bucket || '—',
        views: decisionMetrics(row).views,
        uniqueViews: Number(row.unique_views) || 0,
        submissions: Number(row.submissions) || 0,
        skips: Number(row.skips) || 0,
      })),
    [summary]
  );

  // Use API totals only — do not re-sum category×bucket items
  const totals = summary.totals || {};
  const hasMore = applied.offset + rows.length < total;
  const rangeLabel = [
    applied.date_from || applied.date_to
      ? `${applied.date_from || '…'} → ${applied.date_to || '…'} (IST)`
      : 'All time',
    applied.category ? `· ${applied.category}` : '· all categories',
  ].join(' ');

  const SortHead = ({ id, label, align = 'left' }) => {
    if (!ANALYTICS_SORTS.has(id)) {
      return <TableCell align={align}>{label}</TableCell>;
    }
    return (
      <TableCell align={align} sortDirection={applied.sort === id ? applied.order : false}>
        <TableSortLabel
          active={applied.sort === id}
          direction={applied.sort === id ? applied.order : 'desc'}
          onClick={() => handleSort(id)}
        >
          {label}
        </TableSortLabel>
      </TableCell>
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Pulse analytics</Typography>
        <IconButton onClick={refresh} disabled={summaryLoading || tableLoading} title="Refresh">
          <RefreshIcon />
        </IconButton>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        Date filter is card <code>created_at</code> (IST calendar days, inclusive). Category filter
        applies with the date range on <strong>both</strong> summary and per-card endpoints — never
        dropped when dates are set. <strong>Views</strong> on this page are{' '}
        <code>submissions + skips</code>. <strong>Unique views</strong> are people who opened a card,
        so that number can be higher than submissions. <strong>Unique people</strong> are people who
        submitted, so that number stays at or under submissions. <strong>Awarded people</strong> were
        actually paid (<code>reward_fe &gt; 0</code>). Those three totals are distinct across the
        whole filter — adding category rows counts a person again in every category they touched.
        Interaction % and skip % are shares of views and add to 100. Column sort runs on the full
        filtered set, not the page on screen. FE pending on an expired prediction stays until
        settlement. Fan Wars warrior cards are not included in FE pending.
      </Alert>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
        <TextField
          size="small"
          label="From (IST)"
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 160 }}
        />
        <TextField
          size="small"
          label="To (IST)"
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 160 }}
        />
        <TextField
          select
          size="small"
          label="Category"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          sx={{ minWidth: 160 }}
        >
          {PULSE_CATEGORIES.map((c) => (
            <MenuItem key={c.value || 'all'} value={c.value}>
              {c.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          label="Match ID"
          value={matchId}
          onChange={(e) => setMatchId(e.target.value)}
          sx={{ width: 140 }}
        />
        <Button variant="contained" onClick={applyFilters}>
          Apply
        </Button>
        <Button variant="text" onClick={clearDates} disabled={!dateFrom && !dateTo}>
          Clear dates
        </Button>
        <Typography variant="body2" color="text.secondary">
          Range: {rangeLabel}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {summaryLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={4} md={2}>
              <SummaryCard label="Cards" value={formatNumber(totals.cards)} bgcolor="#EEF2FF" />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <SummaryCard
                label="Views"
                hint="submissions + skips"
                value={formatNumber(decisionMetrics(totals).views)}
                bgcolor="#F0F9FF"
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <SummaryCard
                label="Unique views"
                hint="people who opened"
                value={formatNumber(totals.unique_views)}
                bgcolor="#E0F2FE"
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <SummaryCard label="Skips" value={formatNumber(totals.skips)} bgcolor="#FEF3C7" />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <SummaryCard
                label="Submissions"
                value={formatNumber(totals.submissions)}
                bgcolor="#ECFDF5"
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <SummaryCard
                label="Unique people"
                hint="people who submitted"
                value={formatNumber(totals.unique_submissions)}
                bgcolor="#F0FDFA"
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <SummaryCard
                label="Awarded people"
                value={formatNumber(totals.awarded)}
                bgcolor="#F5F3FF"
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <SummaryCard
                label="FE awarded (points)"
                value={formatNumber(totals.fe_awarded)}
                bgcolor="#FFF7ED"
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <SummaryCard
                label="FE pending (points)"
                value={formatNumber(totals.fe_pending)}
                bgcolor="#F8FAFC"
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <SummaryCard
                label="FE spent (points)"
                value={formatNumber(totals.fe_spent)}
                bgcolor="#FEF2F2"
              />
            </Grid>
          </Grid>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Mix by category
              </Typography>
              {chartData.length === 0 ? (
                <Typography color="text.secondary">No analytics yet.</Typography>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="views" fill="#6366F1" name="Views" radius={[4, 4, 0, 0]} />
                    <Bar
                      dataKey="uniqueViews"
                      fill="#38BDF8"
                      name="Unique views"
                      radius={[4, 4, 0, 0]}
                    />
                    <Bar
                      dataKey="submissions"
                      fill="#0EA5E9"
                      name="Submissions"
                      radius={[4, 4, 0, 0]}
                    />
                    <Bar dataKey="skips" fill="#F59E0B" name="Skips" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
              {summary.items.length > 0 && (
                <TableContainer sx={{ mt: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Category</TableCell>
                        <TableCell>Bucket</TableCell>
                        <TableCell align="right">Cards</TableCell>
                        <TableCell align="right">Views</TableCell>
                        <TableCell align="right">Unique views</TableCell>
                        <TableCell align="right">Skips</TableCell>
                        <TableCell align="right">Submissions</TableCell>
                        <TableCell align="right">Unique people</TableCell>
                        <TableCell align="right">Awarded people</TableCell>
                        <TableCell align="right">FE awarded</TableCell>
                        <TableCell align="right">FE pending</TableCell>
                        <TableCell align="right">FE spent</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {summary.items.map((row) => (
                        <TableRow key={`${row.category}-${row.bucket}`}>
                          <TableCell>{row.category}</TableCell>
                          <TableCell>{row.bucket}</TableCell>
                          <TableCell align="right">{formatNumber(row.cards)}</TableCell>
                          <TableCell align="right">{formatNumber(decisionMetrics(row).views)}</TableCell>
                          <TableCell align="right">{formatNumber(row.unique_views)}</TableCell>
                          <TableCell align="right">{formatNumber(row.skips)}</TableCell>
                          <TableCell align="right">{formatNumber(row.submissions)}</TableCell>
                          <TableCell align="right">{formatNumber(row.unique_submissions)}</TableCell>
                          <TableCell align="right">{formatNumber(row.awarded)}</TableCell>
                          <TableCell align="right">{formatNumber(row.fe_awarded)}</TableCell>
                          <TableCell align="right">{formatNumber(row.fe_pending)}</TableCell>
                          <TableCell align="right">{formatNumber(row.fe_spent)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, gap: 2 }}>
        <Box>
          <Typography variant="h6">Per-card performance</Typography>
          <Typography variant="caption" color="text.secondary">
            Export downloads every matching card as .xlsx, in this sort order, not just this page.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          onClick={handleExport}
          disabled={exporting || tableLoading}
        >
          {exporting ? 'Exporting…' : 'Export .xlsx'}
        </Button>
      </Box>

      {tableLoading && <LinearProgress sx={{ mb: 1 }} />}

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <SortHead id="card_id" label="Card ID" />
              <SortHead id="title" label="Title" />
              <SortHead id="category" label="Category" />
              <SortHead id="status" label="Status" />
              <SortHead id="created_at" label="Created" />
              <SortHead id="expires_at" label="Expires" />
              <SortHead id="views" label="Views" align="right" />
              <SortHead id="unique_views" label="Unique views" align="right" />
              <SortHead id="skips" label="Skips" align="right" />
              <SortHead id="submissions" label="Submissions" align="right" />
              <SortHead id="unique_submissions" label="Unique people" align="right" />
              <SortHead id="interaction_rate" label="Interaction" align="right" />
              <SortHead id="skip_rate" label="Skip" align="right" />
              <SortHead id="awarded" label="Awarded people" align="right" />
              <SortHead id="fe_awarded" label="FE awarded" align="right" />
              <SortHead id="fe_pending" label="FE pending" align="right" />
              <SortHead id="fe_spent" label="FE spent" align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {!tableLoading && rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={17} align="center">
                  No card analytics yet.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => {
                const cardId = row.card_id || row.id || '';
                return (
                <TableRow
                  key={cardId || row.title}
                  hover
                  onClick={() => {
                    if (!cardId) return;
                    navigate(`/admin/pulse/cards/${encodeURIComponent(cardId)}`);
                  }}
                  sx={{ cursor: cardId ? 'pointer' : 'default' }}
                >
                  <TableCell
                    onClick={(event) => event.stopPropagation()}
                    sx={{ maxWidth: 280 }}
                  >
                    {cardId ? (
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                        <Typography
                          component="code"
                          variant="caption"
                          sx={{ fontFamily: 'monospace', wordBreak: 'break-all', userSelect: 'all' }}
                        >
                          {cardId}
                        </Typography>
                        <IconButton
                          size="small"
                          title="Copy card ID"
                          onClick={(event) => copyCardId(cardId, event)}
                        >
                          <ContentCopyIcon fontSize="inherit" />
                        </IconButton>
                      </Box>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      component={cardId ? Link : 'span'}
                      to={cardId ? `/admin/pulse/cards/${encodeURIComponent(cardId)}` : undefined}
                      sx={{ color: 'inherit', textDecoration: 'none' }}
                      onClick={(event) => event.stopPropagation()}
                    >
                      {row.title || 'Untitled'}
                    </Typography>
                    {row.match_id != null && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        Match #{row.match_id}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{row.category || row.card_type || '—'}</TableCell>
                  <TableCell>
                    <Chip size="small" label={row.status} color={statusChipColor(row.status)} />
                  </TableCell>
                  <TableCell>{formatPulseDate(row.created_at)}</TableCell>
                  <TableCell>{formatPulseDate(row.expires_at)}</TableCell>
                  <TableCell align="right">{formatNumber(decisionMetrics(row).views)}</TableCell>
                  <TableCell align="right">{formatNumber(row.unique_views)}</TableCell>
                  <TableCell align="right">{formatNumber(row.skips)}</TableCell>
                  <TableCell align="right">{formatNumber(row.submissions)}</TableCell>
                  <TableCell align="right">{formatNumber(row.unique_submissions)}</TableCell>
                  <TableCell align="right">{formatRate(decisionMetrics(row).interaction_rate)}</TableCell>
                  <TableCell align="right">{formatRate(decisionMetrics(row).skip_rate)}</TableCell>
                  <TableCell align="right">{formatNumber(row.awarded)}</TableCell>
                  <TableCell align="right">{formatNumber(row.fe_awarded)}</TableCell>
                  <TableCell align="right">{formatNumber(row.fe_pending)}</TableCell>
                  <TableCell align="right">{formatNumber(row.fe_spent)}</TableCell>
                </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
        <Button
          disabled={applied.offset <= 0 || tableLoading}
          onClick={() =>
            writeParams({ ...applied, offset: Math.max(0, applied.offset - PAGE_SIZE) })
          }
        >
          Previous
        </Button>
        <Typography variant="body2" color="text.secondary">
          {total === 0
            ? '0 cards'
            : `${applied.offset + 1}–${applied.offset + rows.length} of ${total}`}
        </Typography>
        <Button
          disabled={!hasMore || tableLoading}
          onClick={() => writeParams({ ...applied, offset: applied.offset + PAGE_SIZE })}
        >
          Next
        </Button>
      </Box>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={2500}
        onClose={() => setSnackbar('')}
        message={snackbar}
      />
    </Box>
  );
}

export default PulseAnalytics;
